import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/marsheeApi";

const TYPES = [
  ["idea", "Idea"],
  ["story", "Story"],
  ["emotion", "Emotion"],
  ["decision", "Decision"],
  ["evidence", "Evidence"],
  ["note", "Note"],
];
const TYPE_LABEL = Object.fromEntries(TYPES);

const OUTCOMES = [
  ["record_only", "Record only"],
  ["link_venture", "Link to a venture"],
  ["link_project", "Link to a project"],
  ["decision_support", "Decision support / evidence"],
  ["project_candidate", "Possible future project"],
];
const OUTCOME_LABEL = Object.fromEntries(OUTCOMES);

function Label({ children }) {
  return <div className="mh-label mb-4">{children}</div>;
}

function CaptureForm({ ventures, projects, onCreated, setFlash }) {
  const [type, setType] = useState("note");
  const [body, setBody] = useState("");
  const [title, setTitle] = useState("");
  const [more, setMore] = useState(false);
  const [ventureId, setVentureId] = useState("");
  const [projectId, setProjectId] = useState("");
  const [busy, setBusy] = useState(false);

  const reset = () => {
    setType("note"); setBody(""); setTitle("");
    setMore(false); setVentureId(""); setProjectId("");
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!body.trim()) return;
    setBusy(true);
    try {
      const payload = { capture_type: type, body: body.trim() };
      if (title.trim()) payload.title = title.trim();
      if (projectId) {
        payload.project_id = projectId;
        const proj = projects.find((p) => p.id === projectId);
        if (proj?.venture_id) payload.venture_id = proj.venture_id;
      } else if (ventureId) {
        payload.venture_id = ventureId;
      }
      await api.createCapture(payload);
      reset();
      await onCreated();
      setFlash("Kept.");
    } catch (err) {
      setFlash(err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} data-testid="capture-form">
      <div className="flex flex-wrap items-baseline gap-2 mb-3" data-testid="capture-type-row">
        {TYPES.map(([val, lbl]) => (
          <button
            key={val}
            type="button"
            data-testid={`type-${val}`}
            onClick={() => setType(val)}
            className="mh-label"
            style={{
              background: "none",
              cursor: "pointer",
              paddingBottom: "2px",
              color: type === val ? "var(--mh-text)" : "var(--mh-muted)",
              borderBottom: type === val ? "1px solid var(--mh-accent)" : "1px solid transparent",
            }}
          >
            {lbl}
          </button>
        ))}
      </div>

      <textarea
        className="mh-input"
        data-testid="capture-body"
        value={body}
        onChange={(e) => setBody(e.target.value)}
        placeholder="Keep it before it slips — an idea, a moment, a decision…"
        rows={3}
        style={{ resize: "vertical", lineHeight: 1.55 }}
      />

      {more && (
        <div className="mt-4 space-y-4 mh-fade" style={{ maxWidth: "44ch" }}>
          <div>
            <div className="mh-label">Title (optional)</div>
            <input className="mh-input mt-1" data-testid="capture-title" value={title}
              onChange={(e) => setTitle(e.target.value)} placeholder="A short name, if it helps" />
          </div>
          <div>
            <div className="mh-label">Venture (optional)</div>
            <select className="mh-input mt-1" data-testid="capture-venture" value={ventureId}
              onChange={(e) => { setVentureId(e.target.value); setProjectId(""); }}>
              <option value="">Unfiled</option>
              {ventures.map((v) => <option key={v.id} value={v.id}>{v.title}</option>)}
            </select>
          </div>
          <div>
            <div className="mh-label">Project (optional)</div>
            <select className="mh-input mt-1" data-testid="capture-project" value={projectId}
              onChange={(e) => setProjectId(e.target.value)}>
              <option value="">None</option>
              {projects.map((p) => <option key={p.id} value={p.id}>{p.title}</option>)}
            </select>
          </div>
        </div>
      )}

      <div className="mt-4 flex items-center gap-4">
        <button type="submit" className="mh-btn mh-btn-primary" data-testid="capture-save" disabled={busy || !body.trim()}>
          Keep this
        </button>
        <button type="button" className="mh-label" data-testid="capture-more-toggle"
          onClick={() => setMore(!more)} style={{ background: "none", cursor: "pointer", color: "var(--mh-muted)" }}>
          {more ? "Less" : "Add detail"}
        </button>
      </div>
    </form>
  );
}

function AssocLine({ c, ventures, projects }) {
  const vName = ventures.find((v) => v.id === c.current_venture_id)?.title;
  const pName = projects.find((p) => p.id === c.current_project_id)?.title;
  if (!vName && !pName) return null;
  return (
    <p className="mh-label mt-2" data-testid={`capture-assoc-${c.id}`} style={{ color: "var(--mh-action)" }}>
      {[vName, pName].filter(Boolean).join(" · ")}
    </p>
  );
}

function CaptureRow({ c, ventures, projects, onChange, setFlash }) {
  const [open, setOpen] = useState(false);
  const [outcome, setOutcome] = useState("record_only");
  const [linkVenture, setLinkVenture] = useState("");
  const [linkProject, setLinkProject] = useState("");
  const [busy, setBusy] = useState(false);
  const clarified = c.inbox_state === "clarified";

  const clarify = async () => {
    setBusy(true);
    try {
      const payload = { outcome };
      if (outcome === "link_venture" && linkVenture) payload.current_venture_id = linkVenture;
      if (outcome === "link_project" && linkProject) {
        payload.current_project_id = linkProject;
        const proj = projects.find((p) => p.id === linkProject);
        if (proj?.venture_id) payload.current_venture_id = proj.venture_id;
      }
      await api.clarifyCapture(c.id, payload);
      setOpen(false);
      await onChange();
      setFlash("Clarified.");
    } catch (e) {
      setFlash(e.message);
    } finally {
      setBusy(false);
    }
  };

  const changeVenture = async (val) => {
    try { await api.updateCapture(c.id, { current_venture_id: val || null }); await onChange(); setFlash("Association updated."); }
    catch (e) { setFlash(e.message); }
  };
  const changeProject = async (val) => {
    try {
      const body = { current_project_id: val || null };
      if (val) { const proj = projects.find((p) => p.id === val); if (proj?.venture_id) body.current_venture_id = proj.venture_id; }
      await api.updateCapture(c.id, body); await onChange(); setFlash("Association updated.");
    } catch (e) { setFlash(e.message); }
  };

  const needsPicker = outcome === "link_venture" || outcome === "link_project";

  return (
    <li className="py-6" style={{ borderTop: "1px solid var(--mh-divider)" }} data-testid={`capture-${c.id}`}>
      <button className="text-left w-full" onClick={() => setOpen(!open)} data-testid={`capture-toggle-${c.id}`}>
        <span className="mh-label" style={{ color: clarified ? "var(--mh-muted)" : "var(--mh-accent)" }}>
          {TYPE_LABEL[c.capture_type] || c.capture_type}
          {clarified ? ` · ${OUTCOME_LABEL[c.clarification_outcome] || "Clarified"}` : ""}
        </span>
        {c.title && (
          <p className="mh-serif mt-1" data-testid={`capture-title-${c.id}`} style={{ fontSize: "1.25rem", color: "var(--mh-text)" }}>
            {c.title}
          </p>
        )}
        <p className="mh-sans mt-1" data-testid={`capture-body-${c.id}`}
          style={{ fontSize: "0.98rem", lineHeight: 1.55, color: "var(--mh-action)", whiteSpace: "pre-wrap", wordBreak: "break-word", maxWidth: "60ch" }}>
          {c.body}
        </p>
        <AssocLine c={c} ventures={ventures} projects={projects} />
      </button>

      {open && (
        <div className="mt-5 space-y-5" style={{ maxWidth: "46ch" }} data-testid={`capture-editor-${c.id}`}>
          {!clarified && (
            <div>
              <div className="mh-label">Clarify — what is this, really?</div>
              <div className="mt-2 flex flex-col gap-2">
                {OUTCOMES.map(([val, lbl]) => (
                  <label key={val} className="mh-sans flex items-center gap-2" style={{ fontSize: "0.95rem", color: "var(--mh-text)", cursor: "pointer" }}>
                    <input type="radio" name={`outcome-${c.id}`} data-testid={`outcome-${val}-${c.id}`}
                      checked={outcome === val} onChange={() => setOutcome(val)} />
                    {lbl}
                  </label>
                ))}
              </div>
              {outcome === "link_venture" && (
                <select className="mh-input mt-3" data-testid={`clarify-venture-${c.id}`} value={linkVenture} onChange={(e) => setLinkVenture(e.target.value)}>
                  <option value="">Choose a venture…</option>
                  {ventures.map((v) => <option key={v.id} value={v.id}>{v.title}</option>)}
                </select>
              )}
              {outcome === "link_project" && (
                <select className="mh-input mt-3" data-testid={`clarify-project-${c.id}`} value={linkProject} onChange={(e) => setLinkProject(e.target.value)}>
                  <option value="">Choose a project…</option>
                  {projects.map((p) => <option key={p.id} value={p.id}>{p.title}</option>)}
                </select>
              )}
              <button className="mh-btn mh-btn-primary mt-3" data-testid={`clarify-save-${c.id}`} disabled={busy || (needsPicker && !(outcome === "link_venture" ? linkVenture : linkProject))} onClick={clarify}>
                Save clarification
              </button>
            </div>
          )}

          <div>
            <div className="mh-label">Where it currently belongs</div>
            <div className="mt-2 space-y-3">
              <select className="mh-input" data-testid={`assoc-venture-${c.id}`} value={c.current_venture_id || ""} onChange={(e) => changeVenture(e.target.value)}>
                <option value="">Unfiled</option>
                {ventures.map((v) => <option key={v.id} value={v.id}>{v.title}</option>)}
              </select>
              <select className="mh-input" data-testid={`assoc-project-${c.id}`} value={c.current_project_id || ""} onChange={(e) => changeProject(e.target.value)}>
                <option value="">No project</option>
                {projects.map((p) => <option key={p.id} value={p.id}>{p.title}</option>)}
              </select>
            </div>
          </div>
        </div>
      )}
    </li>
  );
}

export default function Capture() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [flash, setFlash] = useState(null);
  const [captures, setCaptures] = useState([]);
  const [ventures, setVentures] = useState([]);
  const [projects, setProjects] = useState([]);

  const load = useCallback(async () => {
    setError(null);
    try {
      const [caps, vs, ps] = await Promise.all([api.captures(), api.ventures(), api.projects()]);
      setCaptures(caps);
      setVentures(vs);
      setProjects(ps);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const inbox = captures.filter((c) => c.inbox_state === "inbox");
  const clarified = captures.filter((c) => c.inbox_state === "clarified");

  if (loading) {
    return (
      <div className="mx-auto max-w-4xl px-6 sm:px-12 py-16">
        <p className="mh-sans" style={{ color: "var(--mh-muted)" }}>Opening the inbox…</p>
      </div>
    );
  }
  if (error && !captures.length && !ventures.length) {
    return (
      <div className="mx-auto max-w-4xl px-6 sm:px-12 py-16" data-testid="capture-error">
        <p className="mh-serif" style={{ fontSize: "1.3rem" }}>The archive is unreachable right now.</p>
        <p className="mh-sans mt-2" style={{ color: "var(--mh-muted)" }}>{error}</p>
        <button className="mh-btn mh-btn-ghost mt-6" onClick={load}>Try again</button>
      </div>
    );
  }

  return (
    <main data-testid="capture-page">
      {flash && (
        <div className="mx-auto max-w-4xl px-6 sm:px-12 pt-8">
          <div className="mh-fade" data-testid="capture-flash" style={{ borderLeft: "2px solid var(--mh-action)", paddingLeft: "1rem", color: "var(--mh-muted)", fontSize: "0.95rem" }}>{flash}</div>
        </div>
      )}

      <div className="mx-auto max-w-4xl px-6 sm:px-12 pt-10 sm:pt-14 pb-8">
        <Label>Capture</Label>
        <p className="mh-sans" style={{ color: "var(--mh-muted)", maxWidth: "48ch", marginBottom: "1.5rem", fontSize: "0.95rem" }}>
          Keep what matters now. Deciding what it means can wait — nothing here is a task until you say so.
        </p>
        <CaptureForm ventures={ventures} projects={projects} onCreated={load} setFlash={setFlash} />
      </div>

      <div className="mh-band-taupe">
        <div className="mx-auto max-w-4xl px-6 sm:px-12 py-10 sm:py-12">
          <Label>Inbox</Label>
          {inbox.length === 0 ? (
            <p className="mh-sans" data-testid="inbox-empty" style={{ color: "var(--mh-muted)", maxWidth: "42ch" }}>
              Nothing waiting. What you keep will rest here quietly until you choose to make sense of it.
            </p>
          ) : (
            <ul style={{ borderBottom: "1px solid var(--mh-divider)" }} data-testid="inbox-list">
              {inbox.map((c) => (
                <CaptureRow key={c.id} c={c} ventures={ventures} projects={projects} onChange={load} setFlash={setFlash} />
              ))}
            </ul>
          )}
        </div>
      </div>

      {clarified.length > 0 && (
        <div className="mx-auto max-w-4xl px-6 sm:px-12 py-10 sm:py-14">
          <Label>Clarified</Label>
          <ul style={{ borderBottom: "1px solid var(--mh-divider)" }} data-testid="clarified-list">
            {clarified.map((c) => (
              <CaptureRow key={c.id} c={c} ventures={ventures} projects={projects} onChange={load} setFlash={setFlash} />
            ))}
          </ul>
        </div>
      )}
    </main>
  );
}
